/**
 * Pricing slice — Redux Toolkit state + thunks for product- and
 * material-scoped pricing-tier ladders.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   ladders: Record<string, {
 *     tiers: PricingTierDTO[];
 *     status: 'idle' | 'loading' | 'success' | 'error';
 *     error: ApiError | null;
 *     lastFetchedAt: number | null;
 *   }>,
 *   mutations: Record<string, {
 *     status: 'idle' | 'pending' | 'success' | 'error';
 *     error: ApiError | null;
 *   }>,
 * }
 * ```
 *
 * Composite key encoding (INTERNAL — never leaks to the public surface):
 *
 *  - `product:{productId}` for `priceableType === 'Product'`.
 *  - `material:{productId}:{materialId}` for the per-material override
 *    ladder of the same product.
 *
 * Mutation keys augment the ladder key with the tier id when a single
 * tier is being mutated (`product:42:tier:7`, `material:42:11:tier:7`).
 * Unscoped mutation operations (e.g. create where there is no tier id
 * yet) use the bare ladder key.
 *
 * The encoding is purely an implementation detail. Public thunks accept
 * typed scope arguments (`productId: number`, `materialId: number`) and
 * the public selectors take the same shape — callers never see the string
 * key. Per `.claude/rules/frontend-data-flow.md`:
 *
 *  - Capsules MUST NOT import this slice. Containers do.
 *  - Optimistic-delete restoration relies on the container providing the
 *    pre-delete tier object as a thunk argument; the slice does not
 *    snapshot pre-mutation state.
 *  - Errors caught from the api-client are already `ApiError` instances
 *    (CON-PRICE-001 throws via `throwAsApiError`); we forward them
 *    verbatim through `rejectWithValue(error)` so reducer cases get
 *    typed access.
 *
 * CON-PRICE-002.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import * as pricingApi from '@dfq/shared/api/pricing';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  CreateCustomSizePricingRatePayload,
  CreatePricingTierPayload,
  CustomSizePricingRateDTO,
  PredefinedSizeMatrixRowDTO,
  PricingTierDTO,
  SavePredefinedSizeMatrixPayload,
  UpdateCustomSizePricingRatePayload,
  UpdatePricingTierPayload,
} from '@dfq/shared/types/pricing';

// ----------------------------------------------------------------------------
// Composite key encoding (internal)
// ----------------------------------------------------------------------------

/**
 * Build the ladder key for a product-scoped tier ladder.
 *
 * @internal Not exported from the package; callers go through the typed
 * selectors instead.
 */
function productLadderKey(productId: number): string {
  return `product:${productId}`;
}

/**
 * Build the ladder key for a material-scoped (per-product↔material pivot)
 * tier ladder.
 *
 * @internal
 */
function materialLadderKey(productId: number, materialId: number): string {
  return `material:${productId}:${materialId}`;
}

/** Mutation key augmenting a ladder key with a tier id. */
function tierMutationKey(ladderKey: string, tierId: number): string {
  return `${ladderKey}:tier:${tierId}`;
}

/**
 * Key for a config's size × quantity matrix. Config-scoped, not
 * product-scoped: every product sharing the configurator type sells against
 * the same matrix.
 *
 * @internal
 */
function matrixKey(configId: number): string {
  return `config:${configId}`;
}

/**
 * Key for a product's custom-size pricing rate ladder. Product-scoped
 * (mirrors `productLadderKey`) since `CustomSizePricingRateRepository::
 * ALLOWED_PRICEABLE_TYPES` only allows `'Product'` today.
 *
 * @internal
 */
function customSizeRateLadderKey(productId: number): string {
  return `product:${productId}`;
}

// ----------------------------------------------------------------------------
// State types
// ----------------------------------------------------------------------------

export type LadderStatus = 'idle' | 'loading' | 'success' | 'error';
export type MutationStatus = 'idle' | 'pending' | 'success' | 'error';

export interface LadderEntry {
  tiers: PricingTierDTO[];
  status: LadderStatus;
  error: ApiError | null;
  lastFetchedAt: number | null;
}

export interface MutationEntry {
  status: MutationStatus;
  error: ApiError | null;
}

/**
 * A size × quantity price matrix, keyed by product-type config. Kept
 * separate from `ladders` because a matrix is 2-D (sizes × quantity breaks)
 * and would otherwise have to be flattened into the linear tier shape.
 */
export interface MatrixEntry {
  rows: PredefinedSizeMatrixRowDTO[];
  status: LadderStatus;
  error: ApiError | null;
  lastFetchedAt: number | null;
}

/**
 * A product's custom-size pricing rate ladder (PL-STICKER-107) — one row
 * per `[minQty, maxQty)` bracket, each carrying `ratePerSqIn` +
 * `minPricePerUnit` instead of a flat `pricePerUnit`. Kept separate from
 * `ladders` for the same reason `matrices` is: a different row shape than
 * {@link PricingTierDTO}.
 */
export interface CustomSizeRateEntry {
  rates: CustomSizePricingRateDTO[];
  status: LadderStatus;
  error: ApiError | null;
  lastFetchedAt: number | null;
}

export interface PricingState {
  ladders: Record<string, LadderEntry>;
  mutations: Record<string, MutationEntry>;
  /**
   * Optional so state rehydrated from before this key existed (and partial
   * fixtures) stay assignable; the reducer always materialises it.
   */
  matrices?: Record<string, MatrixEntry>;
  /** Optional for the same reason `matrices` is — see above. */
  customSizeRates?: Record<string, CustomSizeRateEntry>;
}

const initialState: PricingState = {
  ladders: {},
  mutations: {},
  matrices: {},
  customSizeRates: {},
};

const IDLE_MATRIX: MatrixEntry = {
  rows: [],
  status: 'idle',
  error: null,
  lastFetchedAt: null,
};

const IDLE_CUSTOM_SIZE_RATE_LADDER: CustomSizeRateEntry = {
  rates: [],
  status: 'idle',
  error: null,
  lastFetchedAt: null,
};

const IDLE_LADDER: LadderEntry = {
  tiers: [],
  status: 'idle',
  error: null,
  lastFetchedAt: null,
};

// ----------------------------------------------------------------------------
// Thunk argument shapes
//
// Public arguments are typed; the slice translates them into composite keys
// internally. No string-typed `scope` parameter appears at the public API.
// ----------------------------------------------------------------------------

export interface ProductScopeArg {
  productId: number;
}

export interface MaterialScopeArg {
  productId: number;
  materialId: number;
}

export interface ProductTierMutationArg {
  productId: number;
  tierId: number;
}

export interface MaterialTierMutationArg {
  productId: number;
  materialId: number;
  tierId: number;
}

export interface CreateProductTierArg {
  productId: number;
  payload: CreatePricingTierPayload;
}

export interface CreateMaterialTierArg {
  productId: number;
  materialId: number;
  payload: CreatePricingTierPayload;
}

export interface UpdateProductTierArg {
  productId: number;
  tierId: number;
  payload: UpdatePricingTierPayload;
  /**
   * Original tier value — captured by the container before dispatch and
   * used by the rejected reducer case to restore state in the (rare)
   * case that an optimistic update slipped through. Today the update
   * thunk does NOT optimistically mutate state (per AC: 409 must
   * preserve the original value), but containers may pass this for
   * forward-compatibility with future optimistic flows.
   */
  previousTier?: PricingTierDTO;
}

export interface UpdateMaterialTierArg extends UpdateProductTierArg {
  materialId: number;
}

export interface DeleteProductTierArg {
  productId: number;
  tierId: number;
  /**
   * The full tier value being deleted, snapshotted by the container at
   * dispatch time. The reducer uses this to restore the entry on
   * rejection. Per the technical note in #428, the container is the
   * source of truth for pre-mutation snapshots — the slice does not
   * remember per-mutation history.
   */
  previousTier: PricingTierDTO;
}

export interface DeleteMaterialTierArg extends DeleteProductTierArg {
  materialId: number;
}

// ----------------------------------------------------------------------------
// Rejection helper — preserves the ApiError thrown by the api-client
// ----------------------------------------------------------------------------

/**
 * Wrap a thrown api-client value so the rejected reducer case sees an
 * `ApiError`. CON-PRICE-001's `throwAsApiError` guarantees every reject
 * is already an `ApiError`; we still defend against bad inputs by
 * coercing anything else into a synthetic `ApiError` so reducers never
 * have to handle a non-`ApiError` rejection payload.
 */
function rejectionFromUnknown(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) {
    return new ApiError({ message: err.message, status: 0 });
  }
  return new ApiError({ message: 'Unknown pricing API error', status: 0 });
}

// ----------------------------------------------------------------------------
// Thunks — fetch
// ----------------------------------------------------------------------------

export const fetchProductTiers = createAsyncThunk<
  PricingTierDTO[],
  number,
  { rejectValue: ApiError }
>('pricing/fetchProductTiers', async (productId, { rejectWithValue }) => {
  try {
    return await pricingApi.listProductTiers(productId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * Load a product's custom-size pricing rate ladder (PL-STICKER-107). Empty
 * array is a valid response — it means the pricing engine falls back to
 * the product's flat tier ladder for custom sizes.
 */
export const fetchCustomSizeRates = createAsyncThunk<
  CustomSizePricingRateDTO[],
  number,
  { rejectValue: ApiError }
>('pricing/fetchCustomSizeRates', async (productId, { rejectWithValue }) => {
  try {
    return await pricingApi.listCustomSizePricingRates(productId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

export const fetchMaterialTiers = createAsyncThunk<
  PricingTierDTO[],
  MaterialScopeArg,
  { rejectValue: ApiError }
>('pricing/fetchMaterialTiers', async ({ productId, materialId }, { rejectWithValue }) => {
  try {
    return await pricingApi.listMaterialTiers(productId, materialId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * Load a config's size × quantity matrix (all sizes incl. inactive, each
 * with its quantity ladder).
 */
export const fetchPredefinedSizeMatrix = createAsyncThunk<
  PredefinedSizeMatrixRowDTO[],
  number,
  { rejectValue: ApiError }
>('pricing/fetchPredefinedSizeMatrix', async (configId, { rejectWithValue }) => {
  try {
    return await pricingApi.getPredefinedSizeMatrix(configId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * Save the whole matrix in one atomic request. Unlike the tier ladders —
 * which diff drafts into per-row create/update/delete calls — the matrix
 * syncs as a single PUT, so a 24-cell edit is one round trip and either
 * fully lands or fully rolls back.
 */
export const savePredefinedSizeMatrix = createAsyncThunk<
  PredefinedSizeMatrixRowDTO[],
  { configId: number; payload: SavePredefinedSizeMatrixPayload },
  { rejectValue: ApiError }
>('pricing/savePredefinedSizeMatrix', async ({ configId, payload }, { rejectWithValue }) => {
  try {
    return await pricingApi.savePredefinedSizeMatrix(configId, payload);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

// ----------------------------------------------------------------------------
// Thunks — create
// ----------------------------------------------------------------------------

export const createProductTier = createAsyncThunk<
  PricingTierDTO,
  CreateProductTierArg,
  { rejectValue: ApiError }
>('pricing/createProductTier', async ({ productId, payload }, { rejectWithValue }) => {
  try {
    return await pricingApi.createProductTier(productId, payload);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

export const createMaterialTier = createAsyncThunk<
  PricingTierDTO,
  CreateMaterialTierArg,
  { rejectValue: ApiError }
>(
  'pricing/createMaterialTier',
  async ({ productId, materialId, payload }, { rejectWithValue }) => {
    try {
      return await pricingApi.createMaterialTier(productId, materialId, payload);
    } catch (err) {
      return rejectWithValue(rejectionFromUnknown(err));
    }
  },
);

// ----------------------------------------------------------------------------
// Thunks — update
// ----------------------------------------------------------------------------

export const updateProductTier = createAsyncThunk<
  PricingTierDTO,
  UpdateProductTierArg,
  { rejectValue: ApiError }
>(
  'pricing/updateProductTier',
  async ({ productId, tierId, payload }, { rejectWithValue }) => {
    try {
      return await pricingApi.updateProductTier(productId, tierId, payload);
    } catch (err) {
      return rejectWithValue(rejectionFromUnknown(err));
    }
  },
);

export const updateMaterialTier = createAsyncThunk<
  PricingTierDTO,
  UpdateMaterialTierArg,
  { rejectValue: ApiError }
>(
  'pricing/updateMaterialTier',
  async ({ productId, materialId, tierId, payload }, { rejectWithValue }) => {
    try {
      return await pricingApi.updateMaterialTier(productId, materialId, tierId, payload);
    } catch (err) {
      return rejectWithValue(rejectionFromUnknown(err));
    }
  },
);

// ----------------------------------------------------------------------------
// Thunks — delete (optimistic)
//
// The pending case removes the tier from local state immediately. The
// rejected case restores the snapshotted `previousTier` (passed by the
// container as a thunk argument) and surfaces the error on `mutations`.
// ----------------------------------------------------------------------------

export const deleteProductTier = createAsyncThunk<
  void,
  DeleteProductTierArg,
  { rejectValue: ApiError }
>('pricing/deleteProductTier', async ({ productId, tierId }, { rejectWithValue }) => {
  try {
    await pricingApi.deleteProductTier(productId, tierId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

export const deleteMaterialTier = createAsyncThunk<
  void,
  DeleteMaterialTierArg,
  { rejectValue: ApiError }
>(
  'pricing/deleteMaterialTier',
  async ({ productId, materialId, tierId }, { rejectWithValue }) => {
    try {
      await pricingApi.deleteMaterialTier(productId, materialId, tierId);
    } catch (err) {
      return rejectWithValue(rejectionFromUnknown(err));
    }
  },
);

// ----------------------------------------------------------------------------
// Composite save thunks — diff draft against saved state, dispatch granular
// create / update / delete thunks via Promise.allSettled.
//
// These live in the slice (not the container/registry) so the mutation logic
// stays alongside the underlying create/update/delete thunks they orchestrate.
// Containers consume them through the PRICING_SCOPES registry (CON-PRICE-004).
// ----------------------------------------------------------------------------

/**
 * Outcome of a composite save — caller (container) maps this into a toast.
 *
 *  - `succeeded` / `failed` are counts the container interpolates into the
 *    summary toast ("3 tiers saved, 1 failed").
 *  - `errors` carries the per-row `ApiError` for any rejected sub-operation
 *    so the container can surface 409 range-overlap details (offending
 *    tier ids, the platform's `error_code`).
 *  - `firstConflictTierIds` is the `id`s of tiers whose save returned a
 *    409 conflict — used by the container to highlight rows in the editor
 *    without re-deriving from the errors list.
 */
export interface SaveTiersResult {
  succeeded: number;
  failed: number;
  errors: ApiError[];
  conflictTierIds: Array<number | 'new'>;
}

interface DiffPlan {
  creates: PricingTierDTO[];
  updates: PricingTierDTO[];
  deletes: PricingTierDTO[];
}

/**
 * Diff a draft array against the saved baseline. Pure — no Redux, no async.
 *
 *  - Tiers with `id === null` → creates.
 *  - Tiers whose `id` matches a baseline row but whose mutable fields
 *    (`minQty`, `maxQty`, `pricePerUnit`) differ → updates.
 *  - Tiers present in the baseline but absent from the draft → deletes.
 *  - Tiers identical to their baseline → no-op (excluded from all three
 *    arrays).
 */
function diffTierDrafts(draft: PricingTierDTO[], baseline: PricingTierDTO[]): DiffPlan {
  const baselineById = new Map<number, PricingTierDTO>();
  for (const tier of baseline) {
    if (tier.id !== null) baselineById.set(tier.id, tier);
  }

  const creates: PricingTierDTO[] = [];
  const updates: PricingTierDTO[] = [];

  for (const row of draft) {
    if (row.id === null) {
      creates.push(row);
      continue;
    }
    const previous = baselineById.get(row.id);
    if (!previous) {
      // Draft references an id we don't know about — treat as create-with-
      // hint so the platform can decide. In practice this should not happen
      // because the editor only assigns ids that came from the server.
      creates.push({ ...row, id: null });
      continue;
    }
    if (
      previous.minQty !== row.minQty ||
      previous.maxQty !== row.maxQty ||
      previous.pricePerUnit !== row.pricePerUnit
    ) {
      updates.push(row);
    }
    baselineById.delete(row.id);
  }

  // Anything left in baselineById is a row the draft removed.
  const deletes: PricingTierDTO[] = Array.from(baselineById.values());

  return { creates, updates, deletes };
}

/** Build the create payload (snake_case) from a draft tier. */
function toCreatePayload(tier: PricingTierDTO): CreatePricingTierPayload {
  return {
    min_qty: tier.minQty,
    max_qty: tier.maxQty,
    price_per_unit: tier.pricePerUnit,
  };
}

/** Build the update payload — always sends all three fields so the platform's
 * `required_without_all` rule is trivially satisfied. */
function toUpdatePayload(tier: PricingTierDTO): UpdatePricingTierPayload {
  return {
    min_qty: tier.minQty,
    max_qty: tier.maxQty,
    price_per_unit: tier.pricePerUnit,
  };
}

export interface SaveProductTiersArg {
  productId: number;
  drafts: PricingTierDTO[];
}

export interface SaveMaterialTiersArg {
  productId: number;
  materialId: number;
  drafts: PricingTierDTO[];
}

/**
 * Composite save for the product-scope ladder. Diffs `drafts` against the
 * current saved ladder in state and dispatches `createProductTier`,
 * `updateProductTier`, `deleteProductTier` for each delta. Uses
 * `Promise.allSettled` so a single 409 doesn't abort the rest of the
 * batch; per-row errors are surfaced in the returned `SaveTiersResult`.
 *
 * Rationale (per ticket #430): keeping this in the slice (alongside the
 * granular thunks it orchestrates) avoids leaking dispatch logic into the
 * container layer while still giving the container a single thunk to
 * dispatch with a typed result. The reducer cases for the underlying
 * thunks already handle state updates (insertSorted on create,
 * replaceTier on update, optimistic-then-restore on delete), so the
 * composite thunk does not register its own reducer cases.
 */
export const saveProductTiers = createAsyncThunk<
  SaveTiersResult,
  SaveProductTiersArg,
  { state: PricingRootStateSlice; rejectValue: ApiError }
>(
  'pricing/saveProductTiers',
  async ({ productId, drafts }, { dispatch, getState }) => {
    const baseline = getState().pricing.ladders[productLadderKey(productId)]?.tiers ?? [];
    const plan = diffTierDrafts(drafts, baseline);

    type SettledOp =
      | { kind: 'create'; result: PromiseSettledResult<unknown>; tier: PricingTierDTO }
      | { kind: 'update'; result: PromiseSettledResult<unknown>; tier: PricingTierDTO }
      | { kind: 'delete'; result: PromiseSettledResult<unknown>; tier: PricingTierDTO };

    const ops: Array<Promise<SettledOp>> = [];

    for (const tier of plan.creates) {
      ops.push(
        dispatch(createProductTier({ productId, payload: toCreatePayload(tier) }))
          .then(
            (action): SettledOp => ({
              kind: 'create',
              tier,
              result:
                action.meta.requestStatus === 'fulfilled'
                  ? { status: 'fulfilled', value: action.payload }
                  : {
                      status: 'rejected',
                      reason:
                        (action.payload as ApiError | undefined) ??
                        new ApiError({ message: 'Create failed', status: 0 }),
                    },
            }),
          ),
      );
    }

    for (const tier of plan.updates) {
      // tierId is non-null on updates by construction (diffTierDrafts only
      // routes ided rows here).
      const tierId = tier.id as number;
      ops.push(
        dispatch(
          updateProductTier({ productId, tierId, payload: toUpdatePayload(tier) }),
        ).then(
          (action): SettledOp => ({
            kind: 'update',
            tier,
            result:
              action.meta.requestStatus === 'fulfilled'
                ? { status: 'fulfilled', value: action.payload }
                : {
                    status: 'rejected',
                    reason:
                      (action.payload as ApiError | undefined) ??
                      new ApiError({ message: 'Update failed', status: 0 }),
                  },
          }),
        ),
      );
    }

    for (const tier of plan.deletes) {
      const tierId = tier.id as number;
      ops.push(
        dispatch(
          deleteProductTier({ productId, tierId, previousTier: tier }),
        ).then(
          (action): SettledOp => ({
            kind: 'delete',
            tier,
            result:
              action.meta.requestStatus === 'fulfilled'
                ? { status: 'fulfilled', value: undefined }
                : {
                    status: 'rejected',
                    reason:
                      (action.payload as ApiError | undefined) ??
                      new ApiError({ message: 'Delete failed', status: 0 }),
                  },
          }),
        ),
      );
    }

    const settled = await Promise.all(ops);

    let succeeded = 0;
    let failed = 0;
    const errors: ApiError[] = [];
    const conflictTierIds: Array<number | 'new'> = [];

    for (const op of settled) {
      if (op.result.status === 'fulfilled') {
        succeeded += 1;
      } else {
        failed += 1;
        const reason = op.result.reason;
        const apiError =
          reason instanceof ApiError
            ? reason
            : new ApiError({
                message:
                  reason instanceof Error ? reason.message : 'Unknown save error',
                status: 0,
              });
        errors.push(apiError);
        if (apiError.status === 409) {
          conflictTierIds.push(op.tier.id ?? 'new');
        }
      }
    }

    return { succeeded, failed, errors, conflictTierIds };
  },
);

export interface SaveCustomSizeRatesArg {
  productId: number;
  drafts: CustomSizePricingRateDTO[];
}

export interface SaveCustomSizeRatesResult {
  succeeded: number;
  failed: number;
  errors: ApiError[];
  conflictRateIds: Array<number | 'new'>;
}

interface CustomSizeRateDiffPlan {
  creates: CustomSizePricingRateDTO[];
  updates: CustomSizePricingRateDTO[];
  deletes: CustomSizePricingRateDTO[];
}

/**
 * Diff a custom-size rate draft array against the saved baseline. Mirrors
 * `diffTierDrafts()` — same create/update/delete-by-id rules — over the
 * rate-bracket field set (`minQty`, `maxQty`, `ratePerSqIn`,
 * `minPricePerUnit`).
 */
function diffRateDrafts(
  draft: CustomSizePricingRateDTO[],
  baseline: CustomSizePricingRateDTO[],
): CustomSizeRateDiffPlan {
  const baselineById = new Map<number, CustomSizePricingRateDTO>();
  for (const rate of baseline) {
    if (rate.id !== null) baselineById.set(rate.id, rate);
  }

  const creates: CustomSizePricingRateDTO[] = [];
  const updates: CustomSizePricingRateDTO[] = [];

  for (const row of draft) {
    if (row.id === null) {
      creates.push(row);
      continue;
    }
    const previous = baselineById.get(row.id);
    if (!previous) {
      creates.push({ ...row, id: null });
      continue;
    }
    if (
      previous.minQty !== row.minQty ||
      previous.maxQty !== row.maxQty ||
      previous.ratePerSqIn !== row.ratePerSqIn ||
      previous.minPricePerUnit !== row.minPricePerUnit
    ) {
      updates.push(row);
    }
    baselineById.delete(row.id);
  }

  const deletes: CustomSizePricingRateDTO[] = Array.from(baselineById.values());

  return { creates, updates, deletes };
}

function toCreateRatePayload(rate: CustomSizePricingRateDTO): CreateCustomSizePricingRatePayload {
  return {
    min_qty: rate.minQty,
    max_qty: rate.maxQty,
    rate_per_sq_in: rate.ratePerSqIn,
    min_price_per_unit: rate.minPricePerUnit,
  };
}

function toUpdateRatePayload(rate: CustomSizePricingRateDTO): UpdateCustomSizePricingRatePayload {
  return {
    min_qty: rate.minQty,
    max_qty: rate.maxQty,
    rate_per_sq_in: rate.ratePerSqIn,
    min_price_per_unit: rate.minPricePerUnit,
  };
}

/**
 * Composite save for a product's custom-size rate ladder (PL-STICKER-107).
 * Diffs `drafts` against the current saved ladder, issues the create /
 * update / delete calls directly through the API client (no per-row
 * thunks — this ladder is small enough that fine-grained optimistic
 * reducer cases aren't worth the ceremony `saveProductTiers` carries), and
 * re-fetches the ladder once every call settles so state always reflects
 * the server's post-write truth. `Promise.allSettled` so one 409 doesn't
 * abort the rest of the batch.
 */
export const saveCustomSizeRates = createAsyncThunk<
  SaveCustomSizeRatesResult,
  SaveCustomSizeRatesArg,
  { state: PricingRootStateSlice; rejectValue: ApiError }
>(
  'pricing/saveCustomSizeRates',
  async ({ productId, drafts }, { dispatch, getState }) => {
    const baseline =
      getState().pricing.customSizeRates?.[customSizeRateLadderKey(productId)]?.rates ?? [];
    const plan = diffRateDrafts(drafts, baseline);

    const ops: Array<Promise<{ id: number | 'new'; result: PromiseSettledResult<unknown> }>> = [];

    for (const rate of plan.creates) {
      ops.push(
        pricingApi
          .createCustomSizePricingRate(productId, toCreateRatePayload(rate))
          .then((value) => ({ id: 'new' as const, result: { status: 'fulfilled', value } as const }))
          .catch((reason: unknown) => ({
            id: 'new' as const,
            result: { status: 'rejected', reason } as const,
          })),
      );
    }

    for (const rate of plan.updates) {
      const rateId = rate.id as number;
      ops.push(
        pricingApi
          .updateCustomSizePricingRate(productId, rateId, toUpdateRatePayload(rate))
          .then((value) => ({ id: rateId, result: { status: 'fulfilled', value } as const }))
          .catch((reason: unknown) => ({ id: rateId, result: { status: 'rejected', reason } as const })),
      );
    }

    for (const rate of plan.deletes) {
      const rateId = rate.id as number;
      ops.push(
        pricingApi
          .deleteCustomSizePricingRate(productId, rateId)
          .then(() => ({ id: rateId, result: { status: 'fulfilled', value: undefined } as const }))
          .catch((reason: unknown) => ({ id: rateId, result: { status: 'rejected', reason } as const })),
      );
    }

    const settled = await Promise.all(ops);

    let succeeded = 0;
    let failed = 0;
    const errors: ApiError[] = [];
    const conflictRateIds: Array<number | 'new'> = [];

    for (const op of settled) {
      if (op.result.status === 'fulfilled') {
        succeeded += 1;
      } else {
        failed += 1;
        const reason = op.result.reason;
        const apiError =
          reason instanceof ApiError
            ? reason
            : new ApiError({
                message: reason instanceof Error ? reason.message : 'Unknown save error',
                status: 0,
              });
        errors.push(apiError);
        if (apiError.status === 409) {
          conflictRateIds.push(op.id);
        }
      }
    }

    // The composite result already tells the caller success/failure; the
    // ladder itself is only trustworthy once re-synced with the server
    // (a create returns the new row's id, which the local draft doesn't
    // have yet).
    await dispatch(fetchCustomSizeRates(productId));

    return { succeeded, failed, errors, conflictRateIds };
  },
);

/**
 * Composite save for the material-override-scope ladder. Mirrors
 * `saveProductTiers` exactly — same diff plan, same `Promise.all` outcome
 * collection, same `SaveTiersResult` shape — but routes the per-row
 * dispatches through the material-scoped thunks (`createMaterialTier`,
 * `updateMaterialTier`, `deleteMaterialTier`).
 *
 * Per the technical note on #431, the material-override editor is the
 * second consumer of the registry pattern; co-locating this thunk with
 * its product-scoped twin keeps the slice as the single home for tier-
 * mutation orchestration so the registry stays a thin adapter.
 */
export const saveMaterialTiers = createAsyncThunk<
  SaveTiersResult,
  SaveMaterialTiersArg,
  { state: PricingRootStateSlice; rejectValue: ApiError }
>(
  'pricing/saveMaterialTiers',
  async ({ productId, materialId, drafts }, { dispatch, getState }) => {
    const baseline =
      getState().pricing.ladders[materialLadderKey(productId, materialId)]?.tiers ?? [];
    const plan = diffTierDrafts(drafts, baseline);

    type SettledOp =
      | { kind: 'create'; result: PromiseSettledResult<unknown>; tier: PricingTierDTO }
      | { kind: 'update'; result: PromiseSettledResult<unknown>; tier: PricingTierDTO }
      | { kind: 'delete'; result: PromiseSettledResult<unknown>; tier: PricingTierDTO };

    const ops: Array<Promise<SettledOp>> = [];

    for (const tier of plan.creates) {
      ops.push(
        dispatch(
          createMaterialTier({ productId, materialId, payload: toCreatePayload(tier) }),
        ).then(
          (action): SettledOp => ({
            kind: 'create',
            tier,
            result:
              action.meta.requestStatus === 'fulfilled'
                ? { status: 'fulfilled', value: action.payload }
                : {
                    status: 'rejected',
                    reason:
                      (action.payload as ApiError | undefined) ??
                      new ApiError({ message: 'Create failed', status: 0 }),
                  },
          }),
        ),
      );
    }

    for (const tier of plan.updates) {
      const tierId = tier.id as number;
      ops.push(
        dispatch(
          updateMaterialTier({
            productId,
            materialId,
            tierId,
            payload: toUpdatePayload(tier),
          }),
        ).then(
          (action): SettledOp => ({
            kind: 'update',
            tier,
            result:
              action.meta.requestStatus === 'fulfilled'
                ? { status: 'fulfilled', value: action.payload }
                : {
                    status: 'rejected',
                    reason:
                      (action.payload as ApiError | undefined) ??
                      new ApiError({ message: 'Update failed', status: 0 }),
                  },
          }),
        ),
      );
    }

    for (const tier of plan.deletes) {
      const tierId = tier.id as number;
      ops.push(
        dispatch(
          deleteMaterialTier({ productId, materialId, tierId, previousTier: tier }),
        ).then(
          (action): SettledOp => ({
            kind: 'delete',
            tier,
            result:
              action.meta.requestStatus === 'fulfilled'
                ? { status: 'fulfilled', value: undefined }
                : {
                    status: 'rejected',
                    reason:
                      (action.payload as ApiError | undefined) ??
                      new ApiError({ message: 'Delete failed', status: 0 }),
                  },
          }),
        ),
      );
    }

    const settled = await Promise.all(ops);

    let succeeded = 0;
    let failed = 0;
    const errors: ApiError[] = [];
    const conflictTierIds: Array<number | 'new'> = [];

    for (const op of settled) {
      if (op.result.status === 'fulfilled') {
        succeeded += 1;
      } else {
        failed += 1;
        const reason = op.result.reason;
        const apiError =
          reason instanceof ApiError
            ? reason
            : new ApiError({
                message:
                  reason instanceof Error ? reason.message : 'Unknown save error',
                status: 0,
              });
        errors.push(apiError);
        if (apiError.status === 409) {
          conflictTierIds.push(op.tier.id ?? 'new');
        }
      }
    }

    return { succeeded, failed, errors, conflictTierIds };
  },
);

// ----------------------------------------------------------------------------
// Reducer helpers
// ----------------------------------------------------------------------------

function ensureLadder(state: PricingState, key: string): LadderEntry {
  if (!state.ladders[key]) {
    state.ladders[key] = { ...IDLE_LADDER };
  }
  return state.ladders[key];
}

function ensureMatrix(state: PricingState, key: string): MatrixEntry {
  state.matrices ??= {};
  if (!state.matrices[key]) {
    state.matrices[key] = { ...IDLE_MATRIX, rows: [] };
  }
  return state.matrices[key];
}

function ensureCustomSizeRateLadder(state: PricingState, key: string): CustomSizeRateEntry {
  state.customSizeRates ??= {};
  if (!state.customSizeRates[key]) {
    state.customSizeRates[key] = { ...IDLE_CUSTOM_SIZE_RATE_LADDER, rates: [] };
  }
  return state.customSizeRates[key];
}

/**
 * Insert a tier into a ladder while preserving the platform's ascending
 * `minQty` ordering. Mirrors the platform's `Repository::list()` order.
 */
function insertSorted(tiers: PricingTierDTO[], tier: PricingTierDTO): PricingTierDTO[] {
  const next = [...tiers, tier];
  next.sort((a, b) => a.minQty - b.minQty);
  return next;
}

function replaceTier(tiers: PricingTierDTO[], updated: PricingTierDTO): PricingTierDTO[] {
  const next = tiers.map((t) => (t.id === updated.id ? updated : t));
  // minQty may have shifted; re-sort to keep the ladder ascending.
  next.sort((a, b) => a.minQty - b.minQty);
  return next;
}

/**
 * Remove every mutation key tied to this ladder. Called after a
 * successful fetch so containers don't see stale error state lingering
 * after a recovered failure. AC: "Slice clears stale `mutations[key]`
 * entries on a successful subsequent fetch of the same ladder."
 */
function clearMutationsForLadder(state: PricingState, ladderKey: string): void {
  const ladderPrefix = ladderKey;
  const tierPrefix = `${ladderKey}:tier:`;
  for (const key of Object.keys(state.mutations)) {
    if (key === ladderPrefix || key.startsWith(tierPrefix)) {
      delete state.mutations[key];
    }
  }
}

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    /**
     * Clear a single mutation entry — used by containers after a toast
     * is dismissed so the next render doesn't re-surface the same
     * error. Takes the typed mutation key components rather than the
     * encoded string.
     */
    clearProductMutation(
      state,
      action: PayloadAction<{ productId: number; tierId?: number }>,
    ) {
      const ladderKey = productLadderKey(action.payload.productId);
      const key = action.payload.tierId
        ? tierMutationKey(ladderKey, action.payload.tierId)
        : ladderKey;
      delete state.mutations[key];
    },
    clearMaterialMutation(
      state,
      action: PayloadAction<{ productId: number; materialId: number; tierId?: number }>,
    ) {
      const ladderKey = materialLadderKey(action.payload.productId, action.payload.materialId);
      const key = action.payload.tierId
        ? tierMutationKey(ladderKey, action.payload.tierId)
        : ladderKey;
      delete state.mutations[key];
    },
  },
  extraReducers(builder) {
    builder
      // -- fetchProductTiers -------------------------------------------------
      .addCase(fetchProductTiers.pending, (state, action) => {
        const key = productLadderKey(action.meta.arg);
        const ladder = ensureLadder(state, key);
        ladder.status = 'loading';
        ladder.error = null;
      })
      .addCase(fetchProductTiers.fulfilled, (state, action) => {
        const key = productLadderKey(action.meta.arg);
        state.ladders[key] = {
          tiers: action.payload,
          status: 'success',
          error: null,
          lastFetchedAt: Date.now(),
        };
        clearMutationsForLadder(state, key);
      })
      .addCase(fetchProductTiers.rejected, (state, action) => {
        const key = productLadderKey(action.meta.arg);
        const ladder = ensureLadder(state, key);
        ladder.status = 'error';
        ladder.error = action.payload ?? rejectionFromUnknown(action.error);
      })
      // -- custom-size pricing rates (PL-STICKER-107) ------------------------
      .addCase(fetchCustomSizeRates.pending, (state, action) => {
        const key = customSizeRateLadderKey(action.meta.arg);
        const ladder = ensureCustomSizeRateLadder(state, key);
        ladder.status = 'loading';
        ladder.error = null;
      })
      .addCase(fetchCustomSizeRates.fulfilled, (state, action) => {
        const key = customSizeRateLadderKey(action.meta.arg);
        state.customSizeRates ??= {};
        state.customSizeRates[key] = {
          rates: action.payload,
          status: 'success',
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchCustomSizeRates.rejected, (state, action) => {
        const key = customSizeRateLadderKey(action.meta.arg);
        const ladder = ensureCustomSizeRateLadder(state, key);
        ladder.status = 'error';
        ladder.error = action.payload ?? rejectionFromUnknown(action.error);
      })
      // -- predefined-size matrix -------------------------------------------
      .addCase(fetchPredefinedSizeMatrix.pending, (state, action) => {
        const entry = ensureMatrix(state, matrixKey(action.meta.arg));
        entry.status = 'loading';
        entry.error = null;
      })
      .addCase(fetchPredefinedSizeMatrix.fulfilled, (state, action) => {
        state.matrices ??= {};
        state.matrices[matrixKey(action.meta.arg)] = {
          rows: action.payload,
          status: 'success',
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchPredefinedSizeMatrix.rejected, (state, action) => {
        const entry = ensureMatrix(state, matrixKey(action.meta.arg));
        entry.status = 'error';
        entry.error = action.payload ?? rejectionFromUnknown(action.error);
      })
      .addCase(savePredefinedSizeMatrix.fulfilled, (state, action) => {
        // The sync returns the persisted matrix, so the response IS the new
        // baseline — no refetch needed.
        state.matrices ??= {};
        state.matrices[matrixKey(action.meta.arg.configId)] = {
          rows: action.payload,
          status: 'success',
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(savePredefinedSizeMatrix.rejected, (state, action) => {
        const entry = ensureMatrix(state, matrixKey(action.meta.arg.configId));
        entry.error = action.payload ?? rejectionFromUnknown(action.error);
      })
      // -- fetchMaterialTiers -----------------------------------------------
      .addCase(fetchMaterialTiers.pending, (state, action) => {
        const key = materialLadderKey(action.meta.arg.productId, action.meta.arg.materialId);
        const ladder = ensureLadder(state, key);
        ladder.status = 'loading';
        ladder.error = null;
      })
      .addCase(fetchMaterialTiers.fulfilled, (state, action) => {
        const key = materialLadderKey(action.meta.arg.productId, action.meta.arg.materialId);
        state.ladders[key] = {
          tiers: action.payload,
          status: 'success',
          error: null,
          lastFetchedAt: Date.now(),
        };
        clearMutationsForLadder(state, key);
      })
      .addCase(fetchMaterialTiers.rejected, (state, action) => {
        const key = materialLadderKey(action.meta.arg.productId, action.meta.arg.materialId);
        const ladder = ensureLadder(state, key);
        ladder.status = 'error';
        ladder.error = action.payload ?? rejectionFromUnknown(action.error);
      })
      // -- createProductTier -------------------------------------------------
      .addCase(createProductTier.pending, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        state.mutations[ladderKey] = { status: 'pending', error: null };
      })
      .addCase(createProductTier.fulfilled, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const ladder = ensureLadder(state, ladderKey);
        ladder.tiers = insertSorted(ladder.tiers, action.payload);
        state.mutations[ladderKey] = { status: 'success', error: null };
      })
      .addCase(createProductTier.rejected, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        state.mutations[ladderKey] = {
          status: 'error',
          error: action.payload ?? rejectionFromUnknown(action.error),
        };
      })
      // -- createMaterialTier -----------------------------------------------
      .addCase(createMaterialTier.pending, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        state.mutations[ladderKey] = { status: 'pending', error: null };
      })
      .addCase(createMaterialTier.fulfilled, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const ladder = ensureLadder(state, ladderKey);
        ladder.tiers = insertSorted(ladder.tiers, action.payload);
        state.mutations[ladderKey] = { status: 'success', error: null };
      })
      .addCase(createMaterialTier.rejected, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        state.mutations[ladderKey] = {
          status: 'error',
          error: action.payload ?? rejectionFromUnknown(action.error),
        };
      })
      // -- updateProductTier -------------------------------------------------
      // No optimistic mutation on update: AC states 409 must preserve the
      // original tier value, so we wait for the server response before
      // changing local state.
      .addCase(updateProductTier.pending, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = { status: 'pending', error: null };
      })
      .addCase(updateProductTier.fulfilled, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        const ladder = state.ladders[ladderKey];
        if (ladder) {
          ladder.tiers = replaceTier(ladder.tiers, action.payload);
        }
        state.mutations[mutKey] = { status: 'success', error: null };
      })
      .addCase(updateProductTier.rejected, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        // Original tier value remains untouched in `state.ladders` because
        // we never optimistically mutated it — the AC is satisfied by the
        // absence of a pending-side write.
        state.mutations[mutKey] = {
          status: 'error',
          error: action.payload ?? rejectionFromUnknown(action.error),
        };
      })
      // -- updateMaterialTier -----------------------------------------------
      .addCase(updateMaterialTier.pending, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = { status: 'pending', error: null };
      })
      .addCase(updateMaterialTier.fulfilled, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        const ladder = state.ladders[ladderKey];
        if (ladder) {
          ladder.tiers = replaceTier(ladder.tiers, action.payload);
        }
        state.mutations[mutKey] = { status: 'success', error: null };
      })
      .addCase(updateMaterialTier.rejected, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = {
          status: 'error',
          error: action.payload ?? rejectionFromUnknown(action.error),
        };
      })
      // -- deleteProductTier (OPTIMISTIC) -----------------------------------
      .addCase(deleteProductTier.pending, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = { status: 'pending', error: null };
        const ladder = state.ladders[ladderKey];
        if (ladder) {
          ladder.tiers = ladder.tiers.filter((t) => t.id !== action.meta.arg.tierId);
        }
      })
      .addCase(deleteProductTier.fulfilled, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = { status: 'success', error: null };
      })
      .addCase(deleteProductTier.rejected, (state, action) => {
        const ladderKey = productLadderKey(action.meta.arg.productId);
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        // Restore the optimistically-removed tier from the snapshot the
        // container provided.
        const ladder = state.ladders[ladderKey];
        if (ladder && action.meta.arg.previousTier) {
          // Guard against double-restore if the tier somehow snuck back in.
          const exists = ladder.tiers.some((t) => t.id === action.meta.arg.previousTier.id);
          if (!exists) {
            ladder.tiers = insertSorted(ladder.tiers, action.meta.arg.previousTier);
          }
        }
        state.mutations[mutKey] = {
          status: 'error',
          error: action.payload ?? rejectionFromUnknown(action.error),
        };
      })
      // -- deleteMaterialTier (OPTIMISTIC) ----------------------------------
      .addCase(deleteMaterialTier.pending, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = { status: 'pending', error: null };
        const ladder = state.ladders[ladderKey];
        if (ladder) {
          ladder.tiers = ladder.tiers.filter((t) => t.id !== action.meta.arg.tierId);
        }
      })
      .addCase(deleteMaterialTier.fulfilled, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        state.mutations[mutKey] = { status: 'success', error: null };
      })
      .addCase(deleteMaterialTier.rejected, (state, action) => {
        const ladderKey = materialLadderKey(
          action.meta.arg.productId,
          action.meta.arg.materialId,
        );
        const mutKey = tierMutationKey(ladderKey, action.meta.arg.tierId);
        const ladder = state.ladders[ladderKey];
        if (ladder && action.meta.arg.previousTier) {
          const exists = ladder.tiers.some((t) => t.id === action.meta.arg.previousTier.id);
          if (!exists) {
            ladder.tiers = insertSorted(ladder.tiers, action.meta.arg.previousTier);
          }
        }
        state.mutations[mutKey] = {
          status: 'error',
          error: action.payload ?? rejectionFromUnknown(action.error),
        };
      });
  },
});

// ----------------------------------------------------------------------------
// Selectors
//
// The composite-key encoding stays internal. Public selectors take the same
// typed scope arguments the thunks do, and return a shape consumers can use
// directly without knowing how the slice indexes its state.
// ----------------------------------------------------------------------------

/**
 * Minimal slice of root state the selectors read. Importing `RootState`
 * here would create a cycle (root reducer imports this slice via the
 * barrel); narrowing the input keeps the slice self-contained.
 */
export interface PricingRootStateSlice {
  pricing: PricingState;
}

/**
 * Public ladder shape — never includes the internal composite key.
 */
export interface PricingLadderView {
  tiers: PricingTierDTO[];
  status: LadderStatus;
  error: ApiError | null;
}

const EMPTY_LADDER_VIEW: PricingLadderView = {
  tiers: [],
  status: 'idle',
  error: null,
};

function viewFromLadder(ladder: LadderEntry | undefined): PricingLadderView {
  if (!ladder) return EMPTY_LADDER_VIEW;
  return { tiers: ladder.tiers, status: ladder.status, error: ladder.error };
}

export function selectProductTierLadder(
  state: PricingRootStateSlice,
  productId: number,
): PricingLadderView {
  return viewFromLadder(state.pricing.ladders[productLadderKey(productId)]);
}

export function selectMaterialTierLadder(
  state: PricingRootStateSlice,
  productId: number,
  materialId: number,
): PricingLadderView {
  return viewFromLadder(state.pricing.ladders[materialLadderKey(productId, materialId)]);
}

/**
 * Mutation selectors — containers consume these to wire toast / inline-
 * error UI to a specific tier mutation. As with the ladder selectors, the
 * composite key stays internal.
 */
export function selectProductTierMutation(
  state: PricingRootStateSlice,
  productId: number,
  tierId?: number,
): MutationEntry | undefined {
  const ladderKey = productLadderKey(productId);
  const key = tierId ? tierMutationKey(ladderKey, tierId) : ladderKey;
  return state.pricing.mutations[key];
}

export function selectMaterialTierMutation(
  state: PricingRootStateSlice,
  productId: number,
  materialId: number,
  tierId?: number,
): MutationEntry | undefined {
  const ladderKey = materialLadderKey(productId, materialId);
  const key = tierId ? tierMutationKey(ladderKey, tierId) : ladderKey;
  return state.pricing.mutations[key];
}

/**
 * Read a config's size × quantity matrix. Returns an idle entry when the
 * matrix has never been fetched, so containers can render a skeleton without
 * null-checking.
 */
export function selectPredefinedSizeMatrix(
  state: PricingRootStateSlice,
  configId: number,
): MatrixEntry {
  return state.pricing.matrices?.[matrixKey(configId)] ?? IDLE_MATRIX;
}

/**
 * Read a product's custom-size pricing rate ladder (PL-STICKER-107).
 * Returns an idle entry when never fetched, so containers can render a
 * skeleton without null-checking.
 */
export function selectCustomSizeRateLadder(
  state: PricingRootStateSlice,
  productId: number,
): CustomSizeRateEntry {
  return (
    state.pricing.customSizeRates?.[customSizeRateLadderKey(productId)] ??
    IDLE_CUSTOM_SIZE_RATE_LADDER
  );
}

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export const { clearProductMutation, clearMaterialMutation } = pricingSlice.actions;

export { pricingSlice };
export const pricingReducer = pricingSlice.reducer;
export default pricingReducer;

/**
 * Test-only escape hatches — exported so reducer/selector unit tests can
 * assert against the same key-encoding the slice uses internally without
 * re-deriving it. Production callers MUST NOT import these.
 *
 * @internal
 */
export const __INTERNAL_FOR_TESTS__ = {
  productLadderKey,
  materialLadderKey,
  tierMutationKey,
};
